import { Color } from "theme/old-design-system/styled-components";
import { ConversationChannel } from "entities/domain/conversations/conversation-domain";
import moment from "moment";

export enum CampaignStatus {
  DRAFT = "draft",
  PENDING = "pending",
  DONE = "done",
}

export interface TemplateForCampaign {
  id: string | null;
  text: string;
}

export interface MessagesAnalytics {
  total: number;
  totalDelivered: number;
  totalFailed: number;
  totalReplied: number;
  totalSegments: number;
}

export default class CampaignDomain {
  id: string | null;

  name: string;

  merchantId: number;

  status: CampaignStatus;

  audienceId: string | null;

  templateId: string | null;

  messageBody: string | null;

  sentAt: string | null;

  scheduledAt: string | null;

  scheduledFor: string | null;

  createdAt: string | null;

  updatedAt: string | null;

  analytics: MessagesAnalytics | null;

  channel: ConversationChannel;

  mediaUrl: string | null;

  mediaType: string | null;

  constructor(
    id: string | null,
    name: string,
    merchantId: number,
    status: CampaignStatus,
    audienceId: string | null,
    templateId: string | null,
    messageBody: string | null,
    sentAt: string | null,
    scheduledAt: string | null,
    scheduledFor: string | null,
    createdAt: string | null,
    updatedAt: string | null,
    analytics: MessagesAnalytics | null,
    channel: ConversationChannel,
    mediaUrl: string | null,
    mediaType: string | null
  ) {
    this.id = id;
    this.name = name;
    this.merchantId = merchantId;
    this.status = status;
    this.audienceId = audienceId;
    this.templateId = templateId;
    this.messageBody = messageBody;
    this.sentAt = sentAt;
    this.scheduledAt = scheduledAt;
    this.scheduledFor = scheduledFor;
    this.createdAt = createdAt;
    this.updatedAt = updatedAt;
    this.analytics = analytics;
    this.channel = channel;
    this.mediaUrl = mediaUrl;
    this.mediaType = mediaType;
  }

  public static getLabelForStatus(status: CampaignStatus): string {
    switch (status) {
      case CampaignStatus.PENDING:
        return "In Progress";
      case CampaignStatus.DRAFT:
        return "Draft / Scheduled";
      case CampaignStatus.DONE:
        return "Sent";
      default:
        return status;
    }
  }

  public static getReplyRateDescription(
    campaign: CampaignDomain
  ): string | null {
    if (campaign.status !== CampaignStatus.DONE || !campaign.analytics) {
      return null;
    }

    return `${campaign.analytics.totalReplied} out of ${campaign.analytics.total}`;
  }

  public static getReplyRateNumber(campaign: CampaignDomain): number {
    if (campaign.status !== CampaignStatus.DONE) {
      return 0;
    }

    return Math.ceil(
      ((campaign.analytics?.totalReplied || 0) /
        (campaign.analytics?.total || 0) || 0) * 100
    );
  }

  public static getReplyRate(campaign: CampaignDomain): string {
    if (campaign.status !== CampaignStatus.DONE) {
      return "-";
    }

    return `${CampaignDomain.getReplyRateNumber(campaign)} %`;
  }

  public static getDescriptionForStatus(campaign: CampaignDomain): {
    description: string;
    color: string;
  } {
    const getDateTime = (dateString: string | null) => {
      if (!dateString) {
        return "";
      }
      return `${moment(dateString).format("DD/MM/YY HH:mm")}`;
    };
    switch (campaign.status) {
      case CampaignStatus.PENDING:
        return {
          description: "Sending",
          color: "orange.400",
        };
      case CampaignStatus.DRAFT:
        if (campaign.scheduledFor) {
          return {
            description: `Scheduled for ${getDateTime(campaign.scheduledFor)}`,
            color: "purple.400",
          };
        }

        return {
          description: `Last edited ${getDateTime(campaign.updatedAt)}`,
          color: "gray.400",
        };
      case CampaignStatus.DONE:
        return {
          description: `Sent ${getDateTime(campaign.sentAt)}`,
          color: "green.400",
        };
      default:
        return {
          description: "N/A",
          color: "gray.400",
        };
    }
  }
}
